import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { AppLayout } from '../../components/layout';
import { blogService, BlogPost as BlogPostType } from '../../services/BlogService';
import { SEO } from '../../components/common/SEO';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 32px;
  color: #3182ce;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.header`
  margin-bottom: 40px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 3rem;
  color: #1a365d;
  margin-bottom: 16px;
  line-height: 1.2;
`;

const Meta = styled.div`
  color: #718096;
  font-size: 1rem;
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  background: #edf2f7;
  border-radius: 12px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 1.5rem;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ArticleBody = styled.article`
  line-height: 1.8;
  color: #2d3748;
  font-size: 1.15rem;

  h2 {
    color: #2c5282;
    margin: 40px 0 20px;
    font-size: 1.8rem;
  }

  p {
    margin-bottom: 24px;
  }

  ul, ol {
    margin-bottom: 24px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 12px;
  }
`;

export const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (slug) {
        const foundPost = await blogService.getPostBySlug(slug);
        setPost(foundPost);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (loading) return <AppLayout><div>Loading post...</div></AppLayout>;
  if (!post) return <AppLayout><div>Post not found. <Link to="/blog">Back to blog</Link></div></AppLayout>;

  return (
    <AppLayout>
      <SEO 
        title={post.meta?.title || post.title}
        description={post.meta?.description || post.excerpt}
        keywords={post.keywords}
        ogTitle={post.title}
        ogDescription={post.excerpt}
        ogImage={post.featuredImage}
      />
      <Container>
        <BackLink to="/blog">← Back to Blog</BackLink>
        <Header>
          <Title>{post.title}</Title>
          <Meta>
            Published on {post.publishedAt?.toDate().toLocaleDateString()} by {post.author}
          </Meta>
        </Header>
        <FeaturedImage>
          {post.featuredImage ? <img src={post.featuredImage} alt={post.title} /> : 'ERP Insights'}
        </FeaturedImage>
        <ArticleBody dangerouslySetInnerHTML={{ __html: post.content }} />
      </Container>
    </AppLayout>
  );
};
